import { useAnalytics } from "@lazarentertainment/analytics-collector"
import {
  Cart,
  Chart,
  Gift,
  SaleTag,
} from "@lazarentertainment/web-design-system/components/iconOutline"
import { useChannelContext } from "client/components/channel"
import { useLayoutContext } from "client/components/layout"
import { Button } from "client/designSystem/Button"
import { useCartContext } from "client/packages/commerce/components/cart/CartContext"
import {
  CommerceView,
  useShopContext,
} from "client/packages/commerce/components/ShopContext"
import {
  Plugin,
  useGlobalContext,
} from "client/packages/utils/GlobalContext"
import { RozyLayout } from "shared/constants/views"
import styled from "styled-components"

const Wrapper = styled.div<{
  $isMobileLayout: boolean
}>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  > :not(:last-child) {
    margin-right: ${(props) =>
      props.$isMobileLayout
        ? props.theme.mobile.buttonMargin
        : props.theme.main.buttonMargin};
  }
`

export const PluginButtonList = () => {
  const { analytics } = useAnalytics()
  const { channelId, broadcastId, recordingId } = useChannelContext()
  const { layout } = useLayoutContext()

  const {
    state: {
      isCommercePluginAvailable,
      isCommercePluginOpen,
      isGiveawaysPluginAvailable,
      isGiveawaysPluginOpen,
      isTargetPluginAvailable,
      isTargetPluginOpen,
      isVotingPluginAvailable,
      isVotingPluginOpen,
    },
    setPluginOpen,
  } = useGlobalContext()

  const { setView: setCommerceView, setAddedProduct } = useShopContext()
  const { cart } = useCartContext()
  const shouldShowCommerceButton =
    isCommercePluginAvailable && layout === RozyLayout.MOBILE
  const shouldShowCommerceCartButton =
    shouldShowCommerceButton ||
    (layout === RozyLayout.MOBILE && !!cart?.totalQuantity)
  const shouldShowGiveawaysButton = isGiveawaysPluginAvailable
  const shouldShowTargetButton = isTargetPluginAvailable
  const shouldShowVotingButton = isVotingPluginAvailable

  const cartQuantity = cart?.totalQuantity ?? 0

  if (
    !shouldShowCommerceButton &&
    !shouldShowCommerceCartButton &&
    !shouldShowGiveawaysButton &&
    !shouldShowVotingButton &&
    !shouldShowTargetButton
  ) {
    return null
  }

  return (
    <Wrapper $isMobileLayout={layout === RozyLayout.MOBILE}>
      {shouldShowCommerceButton && (
        <Button
          variant="primary"
          size="md"
          icon={SaleTag}
          iconOnly
          onClick={() => {
            setPluginOpen(Plugin.COMMERCE, !isCommercePluginOpen)
            setCommerceView(CommerceView.PRODUCT)
          }}
          data-robot="commerce-open-shop"
        />
      )}
      {shouldShowCommerceCartButton && (
        <Button
          variant="overlay"
          size="md"
          icon={Cart}
          iconOnly
          notificationDotText={
            cartQuantity > 0 ? cartQuantity.toString() : undefined
          }
          onClick={() => {
            setCommerceView(CommerceView.CART)
            setAddedProduct(null)
            setPluginOpen(Plugin.COMMERCE, !isCommercePluginOpen)
          }}
          data-robot="voting-open"
        />
      )}
      {shouldShowTargetButton && (
        <Button
          variant="primary"
          size="md"
          icon={SaleTag}
          iconOnly
          onClick={() => {
            analytics.track("target_open_shop", {
              channelId,
              broadcastId,
              recordingId,
            })
            setPluginOpen(Plugin.TARGET, !isTargetPluginOpen)
          }}
          data-robot="target-open-shop"
        />
      )}
      {shouldShowVotingButton && (
        <Button
          variant="overlay"
          size="md"
          icon={Chart}
          iconOnly
          onClick={() => setPluginOpen(Plugin.VOTING, !isVotingPluginOpen)}
          data-robot="voting-open"
        />
      )}
      {shouldShowGiveawaysButton && (
        <Button
          variant="overlay"
          size="md"
          icon={Gift}
          iconOnly
          onClick={() =>
            setPluginOpen(Plugin.GIVEAWAYS, !isGiveawaysPluginOpen)
          }
          data-robot="giveaways-open"
        />
      )}
    </Wrapper>
  )
}

export const VodPluginButtonList = () => {
  const { layout } = useLayoutContext()

  const {
    state: {
      isCommercePluginOpen,
      isCommercePluginAvailable,
      isTargetPluginOpen,
      isTargetPluginAvailable,
    },
    setPluginOpen,
  } = useGlobalContext()
  const { setView: setCommerceView, setAddedProduct } = useShopContext()
  const { cart } = useCartContext()

  const shouldShowCommerceButton =
    isCommercePluginAvailable && layout === RozyLayout.MOBILE
  const shouldShowCommerceCartButton =
    shouldShowCommerceButton ||
    (layout === RozyLayout.MOBILE && !!cart?.totalQuantity)
  const shouldShowTargetButton = isTargetPluginAvailable

  const cartQuantity = cart?.totalQuantity ?? 0

  return (
    <Wrapper $isMobileLayout={layout === RozyLayout.MOBILE}>
      {shouldShowCommerceButton && (
        <Button
          variant="primary"
          size="md"
          icon={SaleTag}
          iconOnly
          onClick={() => {
            setPluginOpen(Plugin.COMMERCE, !isCommercePluginOpen)
            setCommerceView(CommerceView.PRODUCT)
          }}
          data-robot="commerce-open-shop"
        />
      )}
      {shouldShowCommerceCartButton && (
        <Button
          variant="overlay"
          size="md"
          icon={Cart}
          iconOnly
          notificationDotText={
            cartQuantity > 0 ? cartQuantity.toString() : undefined
          }
          onClick={() => {
            setCommerceView(CommerceView.CART)
            setAddedProduct(null)
            setPluginOpen(Plugin.COMMERCE, !isCommercePluginOpen)
          }}
          data-robot="voting-open"
        />
      )}
      {shouldShowTargetButton && (
        <Button
          variant="primary"
          size="md"
          icon={SaleTag}
          iconOnly
          onClick={() => setPluginOpen(Plugin.TARGET, !isTargetPluginOpen)}
          data-robot="target-open-shop"
        />
      )}
    </Wrapper>
  )
}
